import { Component, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { ListTableComponent } from '@ppl/ui';
import { NgZorroModule } from '../../../../../../shared/modules/ng-zorro/ng-zorro.module';
import { CommonModule } from '@angular/common';
import { DialogConfig, DialogService } from '../../../../../../shared/services/dialog.service';
import { TableRowActionsModel } from '../../../../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../../../../shared/interfaces/actions';
import { TimesheetService } from '../../../../../../services/timesheet.service';
import { ListService } from '../../../../../../shared/services/list.service';
import { AccordionComponent } from "@ppl/ui";
import { PDFFormConfig } from '../../../../../../interfaces/config';
import { HttpResponse } from '@angular/common/http';
import { EditpdfComponent } from '../../../../../editpdf/editpdf.component';
import { UserService } from '../../../../../../services/user.service';
import { AddInvoiceComponent } from "../add-invoice/add-invoice.component";
import { InvoiceFilterComponent } from '../../../staff/invoices-staff-list/invoice-filter/invoice-filter.component';
import { DateTimeFormatterService } from '../../../../../../shared/utils/date-time-formatter.service';

@Component({
  selector: 'ppl-invoice-provider-processed-list',
  standalone: true,
  imports: [ListTableComponent, NgZorroModule, CommonModule,
    AccordionComponent, EditpdfComponent, InvoiceFilterComponent, AddInvoiceComponent],
  templateUrl: './invoice-provider-processed-list.component.html',
  styleUrl: './invoice-provider-processed-list.component.css',
})
export class InvoiceProviderProcessedListComponent {

  @Input() consumers: any[] = [];
  @Input() mode: 'add' | 'view' | 'edit' | 'actions' = 'view'
  @Input() provider: any;
  @Input() invoiceId: any;
  @Input() providerId: any;

  @ViewChild('invoiceViewDialog') invoiceViewDialog!: TemplateRef<any>;
  @ViewChild('pdfDialog') pdfDialogRef!: TemplateRef<any>;
  @ViewChild(ListTableComponent) childComponent!: ListTableComponent;

  private dialogSvc = inject(DialogService);
  actionButtons: TableRowActionsModel = new TableRowActions({ view: true });
  appliedFilters: any;
  selectedRow: any;
  viewDialogId: any;
  excludeList = ['serviceDuration', 'payrollStartDate', 'payrollEndDate', 'payrollId', 'providerId', 'headerId'];

  filterFields = [
    { field: 'Timeframe', show: true },
    { field: 'serviceDate', show: true, hideFuturePeriods: true },
    { field: 'status', show: true },
    { field: 'consumerId', show: true },
  ];

  cols = [
    {
      field: 'payPeriod',
      header: 'Pay Period',
      jsonpath: 'payPeriod',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerName',
      header: 'Provider Name',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'providerPplId',
      header: 'Provider PPL ID',
      jsonpath: 'providerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'chequeNumber',
      header: 'Check Number ',
      jsonpath: 'chequeNumber',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'chequeAmount',
      header: 'Check Amount ',
      jsonpath: 'chequeAmount',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  lineItemCols = [
    {
      field: 'invoiceDate',
      header: 'Invoice Date',
      jsonpath: 'invoiceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'invoiceNumber',
      header: 'Invoice Number',
      jsonpath: 'invoiceNumber',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'serviceDates',
      header: 'Service Dates',
      jsonpath: 'serviceDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'consumerName',
      header: 'Participant Name',
      jsonpath: 'consumerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'consumerPplId',
      header: 'Participant PPL ID',
      jsonpath: 'consumerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerName',
      header: 'Provider Name',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'y',
      isSearchable: 'y',
    },
    {
      field: 'providerPplId',
      header: 'Provider PPL ID',
      jsonpath: 'providerPplId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalCost',
      header: 'Gross Amount',
      jsonpath: 'totalCost',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'paidLines',
      header: 'Paid Lines',
      jsonpath: 'paidLines',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'deniedLines',
      header: 'Denied Lines',
      jsonpath: 'deniedLines',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'totalLines',
      header: 'Total Lines',
      jsonpath: 'totalLines',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    }
  ];

  // Tags the Service Dates cell so its comma separated value can render one date per line.
  serviceDatesCellClassResolver = (_rowData: any, col: any): string =>
    col?.field === 'serviceDates' ? 'service-dates-cell' : '';

  programName!: string;
  payPeriod: any[] = [];
  invoices: any[] = [];
  selectedLineItem!: any;
  pdfConfig?: PDFFormConfig;
  pdfFile?: string;
  pdfBlob?: Blob;
  showModal = false;
  modalBody = {
    pdfViewer: ''
  };
  @Input() isReadOnly = false;

  constructor(private listService: ListService, private timesheetService: TimesheetService, private userService: UserService,
    private dateTimeFormatter: DateTimeFormatterService) { }

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    const userDetails = this.userService.getUserDetails();
    const isInRole = selectedProgram?.roles.some((role: any) => role.role_name === 'PPL Staff Tier 1') && userDetails?.usertype_lkp === 'FMS';
    if (isInRole) {
      this.isReadOnly = true;
    }
    this.actionButtons = new TableRowActions({ view: true });
    this.onSearch({});
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 10) {
    if (searchParams.length == 0) {
      //handle the error here
      return;
    }

    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: recordsPerPage,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: [orderBy],
      orderBy: ['desc'],
    };
    return payload;
  }

  searchInvoices(filters: any, startingPageNumber = 1, currentPage = 0, recordsPerPage = 10) {
    const processedPayload = this.buildRequestPayload(
      'invoice-payperiod',
      'payrollScheduleId',
      filters,
      startingPageNumber,
      recordsPerPage
    );
    this.listService
      .getListData(processedPayload, 'claims')
      .subscribe((data: any) => {
        const records = data.responsedata ?? [];
        // De-dupe and sort the merged set, not just this page, so cross-page
        // duplicates are caught and ordering holds across page boundaries.
        if (startingPageNumber > 1) {
          this.invoices = this.processData([...this.invoices, ...records]);
        } else {
          this.invoices = this.processData(records);
        }
        if (this.childComponent) {
          this.childComponent.recordsPerPage = recordsPerPage;
          this.childComponent.resetPagination(startingPageNumber, currentPage);
        }

        this.invoices.forEach((element: any) => {
          element.payPeriod = element.payrollStartDate + ' - ' + element.payrollEndDate;
        });
      });
  }

  rowClicked(event: any) {
    if (event.loaded) {
      event.showLineItem = !event.showLineItem;
      return;
    }

    const subRowFilter = this.appliedFilters.filter((e: any) => !this.excludeList.includes(e.jsonPath));
    subRowFilter.push({
      jsonPath: "payrollScheduleId",
      value: event.payrollScheduleId,
      operator: "Equal"
    });

    subRowFilter.push({
      jsonPath: "providerId",
      value: event.providerId,
      operator: "Equal"
    });

    subRowFilter.push({
      jsonPath: "headerId",
      value: event.headerId,
      operator: "Equal"
    });

    this.selectedRow = event;
    this.getStaffInvoiceLineItems(subRowFilter, event);
  }

  getStaffInvoiceLineItems(filters: any, event: any) {
    const processedPayload = this.buildRequestPayload(
      'invoice-providerAndStaff',
      'payrollScheduleId',
      filters
    );
    this.listService
      .getListData(processedPayload, 'claims')
      .subscribe((data: any) => {
        const lineItems = data.responsedata;
        lineItems?.forEach((lineItem: any) => {
          lineItem.serviceDates = this.dateTimeFormatter.formatServiceDateList(lineItem.serviceDate);
        });
        event.lineItems = lineItems;
        event.showLineItem = true;
        event.loaded = true;
      });
  }

  handleCancel(event: any) {
    if ((Array.isArray(event) && event[0]?.invoiceHeaderId) || event?.isCloseTriggeredByDialog) {
      this.selectedLineItem = undefined;
    }
  }

  onPageLoad(event: any) {
    this.searchInvoices(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, event.rows ?? 10);
  }

  processData(data: any) {
    if (data && data.length > 0) {
      data.forEach((element: any) => {
        element.payPeriod = element.payrollStartDate + ' - ' + element.payrollEndDate
      });

      data.sort((a: any, b: any) => {
        const dateA = new Date(a.payrollStartDate);
        const dateB = new Date(b.payrollStartDate);
        return dateA.getTime() - dateB.getTime(); // Sorts in ascending order
      });

      return data;
    }

    return data;
  }

  onSearch(event: any) {
    const filters = [];
    // filters.push({
    //   jsonPath: "serviceDuration",
    //   value: event.Timeframe ? event.Timeframe : '30 days',
    //   operator: "lessThan"
    // });

    filters.push({
      jsonPath: "providerId",
      value: this.providerId,
      operator: "Equal"
    });

    const defaultDateFilter = new Date();
    defaultDateFilter.setDate(defaultDateFilter.getDate() - 30);
    filters.push({
      jsonPath: "serviceDate",
      value: event.Timeframe ? event.Timeframe : defaultDateFilter.toLocaleDateString('en-US'),
      operator: "greaterThan"
    });

    // The picker hands back a Date; the list API expects serviceDate as MM/dd/yyyy.
    if (event.serviceDate) {
      filters.push({
        jsonPath: "serviceDate",
        value: this.dateTimeFormatter.formatDateToString(event.serviceDate as Date),
        operator: "Equal"
      });
    }

    if (event.consumerId) {
      filters.push({
        jsonPath: "consumerId",
        value: event.consumerId,
        operator: "Equal"
      });
    }

    if (event.status) {
      filters.push({
        jsonPath: "businessStatus",
        value: event.status,
        operator: "Equal"
      });
    } else {
      filters.push({
        jsonPath: "businessStatus",
        values: ['P', 'CD', 'D'],
        operator: "In"
      });
    }

    if (event.payPeriod) {
      filters.push({
        jsonPath: "paidPayrollScheduleId",
        value: event.payPeriod,
        operator: "Equal"
      });
    }

    this.appliedFilters = filters;
    this.searchInvoices(filters, undefined, undefined, this.childComponent?.recordsPerPage ?? 10);
  }

  onAction(event: any) {
    if (event.action === 'view') {
      this.selectedLineItem = event.data;
      this.invoiceId = event.data.invoiceHeaderId;
      this.onView();
    }
  }

  onView() {
    this.openViewPopup();
  }

  onProcessComplete(event: any) {
    this.dialogSvc.closeDialog(this.viewDialogId);
    this.viewDialogId = null;
  }

  openViewPopup() {
    const dialogConfig: DialogConfig = {
      title: 'Invoice',
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.viewDialogId = this.dialogSvc.openDialog(
      this.invoiceViewDialog,
      dialogConfig,
      () => this.handleCancel({ isCloseTriggeredByDialog: true }),
      () => this.handleCancel({ isCloseTriggeredByDialog: true })
    );
  }


  rowActions(event: any) {
    if (event.type === 'download') {
      this.timesheetService.downloadPaystub(event, this.programName);
    } else {
      this.viewPaystub(event, this.programName);
    }
  }

  viewPaystub(event: any, programCode: string) {
    this.showModal = true;
    this.modalBody.pdfViewer = 'Paystub';

    this.pdfConfig = {
      form_id: '',
      is_mandatory: false,
      form_description: '',
      form_name: '',
      fields: []
    };

    const dialogConfig: DialogConfig = {
      title: this.modalBody.pdfViewer,
      okText: 'Save',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    }

    this.dialogSvc.openDialog(
      this.pdfDialogRef,
      dialogConfig,
      () => this.handlePDFDialogCancel(),
      () => this.handlePDFDialogCancel()
    );

    this.getPaystub(event.event.checkId, event.event.chequeNumber, event.event.providerPplId, this.programName);
  }

  getPaystub(checkId: string, chequeNumber: string, empId: string, programCode: string) {
    this.timesheetService.getPayStub(checkId, chequeNumber, empId, programCode).subscribe((response: HttpResponse<Blob>) => {
      if (response.body) {
        const blob = new Blob([response.body], { type: response.body.type });
        this.pdfBlob = blob;
        this.pdfFile = window.URL.createObjectURL(blob);
      }
    });
  }

  handlePDFDialogCancel() {
    this.showModal = false;
    this.pdfConfig = undefined;
    this.pdfFile = undefined;
    this.pdfBlob = undefined;
  }

  fieldsUpdated(event: any) {
    //handle fields updated event if needed
  }
}
